import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";

import EcrfBlocks from "../../../../components/Trials/EcrfBlocks";
import ScheduleVisits from "../../../../components/Trials/ScheduleVisits";
import SpecialVisitSession from "../../../../components/Ecrf/SpecialVisitSession";
import SurveysModule from "../../../../components/TrialAccordions/SurveysModule";
import AdvancedSurveysModule from "../../../../components/TrialAccordions/AdvancedSurveysModule";
import VideosModule from "../../../../components/TrialAccordions/VideosModule";
import PhotosModule from "../../../../components/TrialAccordions/PhotosModule";
import AIAssessmentModule from "../../../../components/TrialAccordions/AIAssessmentModule";
import InstructionsModule from "../../../../components/TrialAccordions/InstructionsModule";
import PatientEdiaryPanel from "../../PatientEdiaryPanel";
import logUserAction from "../../../../apis/userLogger";
import { useEcrfContext } from "../../../../context/EcrfContext";
import useGetPerVisitEcrfBlocks from "../../../../hooks/useGetPerVisitEcrfBlocks";
import useVisitPhotoTypes from "../../../../hooks/useVisitPhotoTypes";
import useVisitAssessmentAreas from "../../../../hooks/useVisitAssessmentAreas";
import exportEcrfSourceDoc from "../../../../utils/exportEcrfSourceDoc";
import { getVisibleSteps } from "../../../../utils/visitSteps";
import { USER_ROLES } from "../../../../utils/constants";

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <Box component="section">
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Icon fontSize="small" color="primary" />
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Stack>
    {children}
  </Box>
);

const BlockStatusIcon = ({ block }) => {
  if (block.possible_incorrect_data) return <ErrorOutlineIcon fontSize="small" color="error" />;
  if (block.has_constraint_error) return <HelpOutlineIcon fontSize="small" color="error" />;
  if (block.is_completed) return <CheckCircleIcon fontSize="small" color="success" />;
  return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "text.disabled" }} />;
};

const EmptyState = ({ children }) => (
  <Paper
    variant="outlined"
    sx={{ p: 3, borderRadius: 2, borderStyle: "dashed", textAlign: "center" }}
  >
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Paper>
);

const VisitDetails = ({
  role,
  guard,
  setHasUnsavedEcrfChanges,
  setMessage,
  setTypeOfMessage,
  modalVisitChangeShow,
  setModalVisitChangeShow,
  setScheduleBaselineModalShow,
  singlePhotoSession,
}) => {
  const { infoData } = useOutletContext();
  const {
    selectedVisit,
    trial,
    patientId,
    selectedSessionId,
    setSurveyData,
    photoTypes,
    setPhotoTypes,
  } = useEcrfContext();

  const [selectedBlock, setSelectedBlock] = useState(null);
  const [newForm, setNewForm] = useState(false);
  const [searchTrailType, setSearchTrailType] = useState("");
  const [searchBlockName, setSearchBlockName] = useState("");

  const visitNumber = selectedVisit?.visit_number;
  const isEventVisit = visitNumber === -1 || visitNumber === -2;
  const isEdiary = visitNumber === -6;
  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);

  const { data: perVisitBlocks, isLoading: isLoadingBlocks } = useGetPerVisitEcrfBlocks({
    visitId: selectedVisit?.id,
    patientId,
    sessionId: selectedSessionId,
    enabled: !isEdiary && (!isEventVisit || Boolean(selectedSessionId)),
  });
  const blocks = perVisitBlocks?.results ?? [];

  useEffect(() => {
    // Event sessions and the source-doc export read the blocks from the eCRF context, as in v1
    if (perVisitBlocks) setSurveyData(perVisitBlocks.results);
  }, [perVisitBlocks, setSurveyData]);

  const { data: photoTypesData } = useVisitPhotoTypes({
    trialId: trial?.id,
    visitId: selectedVisit?.id,
    enabled: !!trial?.id && !!selectedVisit?.id,
  });

  useEffect(() => {
    if (photoTypesData) setPhotoTypes(photoTypesData);
  }, [photoTypesData, setPhotoTypes]);

  const { data: assessmentAreas } = useVisitAssessmentAreas({
    visitId: selectedVisit?.id,
    trialId: null,
  });

  const handleSelectBlock = (blockName, isNewBlock) =>
    guard(() => {
      setNewForm(isNewBlock);
      setSelectedBlock(blockName);
    });

  const toggleBlock = (block) => {
    if (selectedBlock === block.name) {
      guard(() => setSelectedBlock(null));
      return;
    }
    logUserAction(
      `Selected eCRF block "${block.name}" in visit "${selectedVisit?.visit_name}"`,
      `${infoData?.email}`,
      `${trial?.name}`
    );
    handleSelectBlock(block.name, block.new_block);
  };

  const exportSourceDoc = (includeAnswers) => {
    exportEcrfSourceDoc({
      surveyData: blocks,
      visit: selectedVisit,
      trial,
      infoData,
      includeAnswers,
      includeIds: !includeAnswers,
    });
    logUserAction(
      includeAnswers ? 'Clicked "Export as Filled Source Doc"' : 'Clicked "Export as Source Doc"',
      "eCRF Visit Cards"
    );
  };

  if (isEdiary) {
    return (
      <PatientEdiaryPanel patientId={patientId} trialId={trial?.id} isTrialLocked={isTrialLocked} />
    );
  }

  const showVisitDetails = !isEventVisit && visitNumber !== -4 && role !== USER_ROLES.REVIEWER;
  const showAppActivity = !isEventVisit && visitNumber !== -4 && role !== USER_ROLES.REVIEWER;
  const steps = getVisibleSteps(trial);
  const moduleProps = {
    role,
    selectedSessionId,
    selectedVisit,
    patientId,
    visitId: selectedVisit?.id,
    isTrialLocked,
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
        <Button
          size="small"
          variant="outlined"
          startIcon={<PictureAsPdfOutlinedIcon />}
          disabled={!blocks.length}
          onClick={() => exportSourceDoc(false)}
        >
          Export as Source Doc
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<PictureAsPdfOutlinedIcon />}
          disabled={!blocks.length}
          onClick={() => exportSourceDoc(true)}
        >
          Export as Filled Source Doc
        </Button>
      </Stack>

      {showVisitDetails && (
        <Section icon={EventNoteOutlinedIcon} title="Visit details">
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
            <ScheduleVisits
              embedded
              setScheduleBaselineModalShow={setScheduleBaselineModalShow}
              singlePhotoSession={singlePhotoSession}
            />
          </Paper>
        </Section>
      )}

      {isEventVisit ? (
        <SpecialVisitSession
          selectedBlock={selectedBlock}
          setSelectedBlock={setSelectedBlock}
          handleSelectBlock={handleSelectBlock}
          setMessage={setMessage}
          setTypeOfMessage={setTypeOfMessage}
          modalVisitChangeShow={modalVisitChangeShow}
          setModalVisitChangeShow={setModalVisitChangeShow}
          searchTrailType={searchTrailType}
          setSearchTrailType={setSearchTrailType}
          searchBlockName={searchBlockName}
          setSearchBlockName={setSearchBlockName}
          newForm={newForm}
          setNewForm={setNewForm}
          setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
        />
      ) : (
        <Section
          icon={LocalHospitalOutlinedIcon}
          title="In-clinic activity"
          subtitle={
            blocks.length
              ? `${blocks.filter((b) => b.is_completed).length}/${blocks.length} forms completed`
              : null
          }
        >
          {isLoadingBlocks ? (
            <Stack spacing={1}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={52} />
              ))}
            </Stack>
          ) : blocks.length ? (
            <Stack spacing={1}>
              {blocks.map((block) => (
                <Accordion
                  key={block.id}
                  expanded={selectedBlock === block.name}
                  onChange={() => toggleBlock(block)}
                  disableGutters
                  elevation={0}
                  TransitionProps={{ unmountOnExit: true }}
                  sx={{
                    border: 1,
                    borderColor: selectedBlock === block.name ? "primary.main" : "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      <BlockStatusIcon block={block} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                        data-ecrf-block={block.name}
                      >
                        {block.name}
                      </Typography>
                      {block.is_completed && (
                        <Chip
                          size="small"
                          label="Completed"
                          color="success"
                          variant="outlined"
                          sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        />
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, borderTop: 1, borderColor: "divider" }}
                  >
                    {block.new_block ? (
                      <EcrfBlocks
                        visit_number={visitNumber}
                        visitName={selectedVisit?.visit_name}
                        visitId={selectedVisit?.id}
                        photo_session={selectedVisit?.photo_session}
                        selectedBlock={block.name}
                        setMessage={setMessage}
                        newForm={newForm}
                        setTypeOfMessage={setTypeOfMessage}
                        setBlockId={() => {}}
                        patientId={patientId}
                        trialId={trial?.id}
                        trialName={trial?.name}
                        allowCopyingFromOtherVisitsEcrfs={trial?.allow_copying_other_visits_ecrfs}
                        selectedSessionId={selectedSessionId}
                        photoSessionId={selectedVisit?.photo_session_id}
                        setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
                        show_incomplete_date_warning_on_ecrf={
                          trial?.show_incomplete_date_warning_on_ecrf
                        }
                        isTrialLocked={isTrialLocked}
                      />
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        This is a legacy form that is only available in the V1 design.
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : (
            <EmptyState>No eCRF forms are assigned to this visit.</EmptyState>
          )}
        </Section>
      )}

      {showAppActivity && (
        <Section icon={PhoneIphoneOutlinedIcon} title="App activity">
          {steps.length ? (
            <Stack spacing={1}>
              {steps.map((step) => {
                const key = step.id ?? `${step.module}-${step.position ?? 0}`;
                const props = { ...moduleProps, step };
                let module = null;
                if (step.module === "POEM") module = <SurveysModule {...props} />;
                else if (step.module === "Advanced Surveys")
                  module = <AdvancedSurveysModule {...props} />;
                else if (step.module === "Videos") module = <VideosModule {...props} />;
                else if (step.module === "Photos")
                  module = <PhotosModule {...props} photoTypes={photoTypes} />;
                else if (step.module === "AI Assessment")
                  module = (
                    <AIAssessmentModule
                      {...props}
                      trial={trial}
                      assessmentAreas={assessmentAreas}
                    />
                  );
                else if (step.module === "Instructions")
                  module = <InstructionsModule {...props} trial={trial} />;

                // Modules hide themselves for staff when they are not configured; :empty drops the frame too
                return (
                  <Paper
                    key={key}
                    variant="outlined"
                    sx={{ px: 2, py: 1, borderRadius: 2, "&:empty": { display: "none" } }}
                  >
                    {module}
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <EmptyState>No app activities are configured for this trial.</EmptyState>
          )}
        </Section>
      )}
    </Stack>
  );
};

export default VisitDetails;
